<template>
  <v-dialog v-model="dialog" max-width="900" persistent>
    <v-card>
      <v-card-title class="headline">
        Adicionar valores pagos via CSV
        <v-spacer></v-spacer>
        <v-btn icon @click="close" :disabled="processing.status === 'running'">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <div v-if="step === 'upload'">
          <v-file-input
            v-model="file"
            label="Selecione o arquivo CSV"
            accept=".csv"
            prepend-icon="mdi-file-delimited"
            outlined
            @change="onFileSelected"
          ></v-file-input>

          <v-btn text color="primary" @click="downloadTemplate">
            <v-icon left>mdi-download</v-icon>
            Baixar modelo CSV
          </v-btn>

          <div v-if="csvData.length > 0" class="mt-4">
            <v-alert type="info" dense>
              {{ csvData.length }} linhas encontradas
            </v-alert>

            <v-simple-table dense class="mt-2">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>Joined ID</th>
                    <th>Valor pago</th>
                    <th>Competencia</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, i) in csvData.slice(0, 5)" :key="i">
                    <td>{{ row['joined_id'] }}</td>
                    <td>{{ row['valor_pago'] }}</td>
                    <td>{{ row['competencia'] }}</td>
                  </tr>
                  <tr v-if="csvData.length > 5">
                    <td colspan="3" class="text-center caption">... e mais {{ csvData.length - 5 }} linhas</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>
        </div>

        <div v-if="step === 'processing'">
          <v-progress-linear
            :value="(processing.current / processing.total) * 100"
            height="25"
            color="primary"
          >
            <strong>{{ processing.current }} / {{ processing.total }}</strong>
          </v-progress-linear>

          <div class="mt-4">
            <v-chip small color="success" class="mr-2">
              {{ results.success.length }} sucesso
            </v-chip>
            <v-chip small color="error">
              {{ results.errors.length }} erros
            </v-chip>
          </div>

          <div class="mt-4 pa-3 grey lighten-4" style="max-height: 200px; overflow-y: auto;">
            <div v-for="(log, i) in activityLog.slice(-10)" :key="i" class="text-caption">
              {{ log }}
            </div>
          </div>
        </div>

        <div v-if="step === 'results'">
          <v-alert type="success" v-if="results.errors.length === 0">
            Importação concluída com sucesso!
          </v-alert>
          <v-alert type="warning" v-else>
            Importação concluída com {{ results.errors.length }} erro(s)
          </v-alert>

          <v-expansion-panels class="mt-4">
            <v-expansion-panel v-if="results.success.length > 0">
              <v-expansion-panel-header>
                Sucessos ({{ results.success.length }})
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-simple-table dense>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th>Linha</th>
                        <th>Joined ID</th>
                        <th>Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in results.success" :key="item.row">
                        <td>{{ item.row }}</td>
                        <td>{{ item.joinedId }}</td>
                        <td>{{ item.value }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel v-if="results.errors.length > 0">
              <v-expansion-panel-header>
                Erros ({{ results.errors.length }})
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-simple-table dense>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th>Linha</th>
                        <th>Joined ID</th>
                        <th>Erro</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in results.errors" :key="item.row">
                        <td>{{ item.row }}</td>
                        <td>{{ item.joinedId }}</td>
                        <td class="error--text">{{ item.message }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="close" v-if="step !== 'processing'">
          Fechar
        </v-btn>
        <v-btn
          color="primary"
          @click="startProcessing"
          :disabled="csvData.length === 0 || processing.status === 'running'"
          v-if="step === 'upload'"
        >
          Processar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios'
import XLSX from 'xlsx'
import { mapMutations } from 'vuex'

export default {
  name: 'CSVImportPaidValuesDialog',
  props: {
    visible: { type: Boolean, required: true },
    celebrationId: { type: String, required: true }
  },
  data: () => ({
    dialog: false,
    step: 'upload',
    file: null,
    csvData: [],
    processing: {
      current: 0,
      total: 0,
      status: 'idle'
    },
    results: {
      success: [],
      errors: []
    },
    activityLog: []
  }),
  methods: {
    ...mapMutations(['SET_SNACKBAR']),

    onFileSelected (file) {
      if (!file) {
        this.csvData = []
        return
      }

      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result)
          const workbook = XLSX.read(data, { type: 'array', codepage: 65001 })
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]]
          this.csvData = XLSX.utils.sheet_to_json(firstSheet, { raw: false, defval: '' })

          if (this.csvData.length === 0) {
            this.SET_SNACKBAR({ text: 'Arquivo CSV vazio' })
          }
        } catch (error) {
          this.SET_SNACKBAR({ text: 'Erro ao ler arquivo CSV: ' + error.message })
          this.csvData = []
        }
      }
      reader.readAsArrayBuffer(file)
    },

    downloadTemplate () {
      const template = [
        ['joined_id', 'valor_pago', 'competencia'],
        ['xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', '500.00', '01/01/2026']
      ]
      const ws = XLSX.utils.aoa_to_sheet(template)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Modelo')
      XLSX.writeFile(wb, 'modelo_valores_pagos.csv')
    },

    async startProcessing () {
      this.step = 'processing'
      this.processing = {
        current: 0,
        total: this.csvData.length,
        status: 'running'
      }
      this.results = { success: [], errors: [] }
      this.activityLog = []

      for (let i = 0; i < this.csvData.length; i++) {
        const row = this.csvData[i]
        const rowNumber = i + 2

        this.addLog(`Processando linha ${rowNumber}...`)

        try {
          await this.processRow(row, rowNumber)
        } catch (error) {
          let errorMessage = error.message || 'Erro desconhecido'
          if (error.response && error.response.data) {
            const data = error.response.data
            if (typeof data === 'string') {
              errorMessage = data
            } else if (data.message) {
              errorMessage = data.message
            } else if (data.errors) {
              errorMessage = Object.values(data.errors).flat().join(', ')
            } else if (data.error) {
              errorMessage = data.error
            } else {
              errorMessage = JSON.stringify(data)
            }
          }
          this.results.errors.push({
            row: rowNumber,
            joinedId: row['joined_id'] || 'N/A',
            message: errorMessage
          })
          this.addLog(`Linha ${rowNumber}: ${errorMessage}`)
        }

        this.processing.current = i + 1
      }

      this.processing.status = 'completed'
      this.step = 'results'
      this.$emit('imported')
    },

    async processRow (row, rowNumber) {
      const joinedId = (row['joined_id'] || '').trim()
      const valorPago = parseFloat(row['valor_pago'])
      const competencia = (row['competencia'] || '').trim()

      if (!joinedId) {
        throw new Error('joined_id é obrigatório')
      }
      if (!valorPago || valorPago <= 0) {
        throw new Error('valor_pago deve ser maior que zero')
      }
      if (!competencia) {
        throw new Error('competencia é obrigatória')
      }

      // Buscar dados do joined para obter participant_id
      const joinedResponse = await axios.get(`joineds/${joinedId}`)
      const joined = joinedResponse.data
      const participantId = joined.participant_id

      if (!participantId) {
        throw new Error('Não foi possível encontrar o participante para o joined_id informado')
      }

      const invoiceDate = this.parseDate(competencia)
      if (!invoiceDate.isValid()) {
        throw new Error('Data de competência inválida: ' + competencia)
      }

      const invoiceData = {
        celebration_id: this.celebrationId,
        participant_id: participantId,
        invoice_date: invoiceDate.format('YYYY-MM-DD'),
        payment_date: invoiceDate.format('YYYY-MM-DD'),
        due_date: invoiceDate.format('YYYY-MM-DD'),
        items: [{
          category: 'fundraising',
          joined_id: joinedId,
          description: 'Valor pago por fora',
          installment_amount: valorPago
        }]
      }

      await axios.post('invoices/paid/third-parties', invoiceData)

      this.results.success.push({
        row: rowNumber,
        joinedId: joinedId,
        value: valorPago
      })
      this.addLog(`Linha ${rowNumber}: Valor pago registrado`)
    },

    parseDate (dateStr) {
      if (!dateStr) return this.$moment.invalid()

      dateStr = String(dateStr)

      // Formato DD/MM/YYYY
      if (dateStr.includes('/')) {
        const parts = dateStr.split('/')
        if (parts.length === 3) {
          return this.$moment(`${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`, 'YYYY-MM-DD')
        }
      }

      // Formato YYYY-MM-DD
      if (dateStr.includes('-')) {
        return this.$moment(dateStr, 'YYYY-MM-DD')
      }

      // Serial do Excel
      if (!isNaN(dateStr)) {
        return this.$moment(new Date((parseFloat(dateStr) - 25569) * 86400 * 1000))
      }

      return this.$moment.invalid()
    },

    addLog (message) {
      this.activityLog.push(`${this.$moment().format('HH:mm:ss')} - ${message}`)
    },

    close () {
      if (this.processing.status === 'running') return

      this.step = 'upload'
      this.file = null
      this.csvData = []
      this.processing = { current: 0, total: 0, status: 'idle' }
      this.results = { success: [], errors: [] }
      this.activityLog = []

      this.$emit('close')
    }
  },

  watch: {
    visible (val) {
      this.dialog = !!val
    },
    dialog (val) {
      if (!val) this.close()
    }
  }
}
</script>
